import React, { memo } from 'react';
import { motion } from 'framer-motion';

const ValueProposition: React.FC = memo(() => {
    return (
        <section
            className="section-stage relative min-h-[90vh] flex items-center justify-center py-32 px-6 lg:px-8 overflow-hidden isolate"
            aria-label="Value Proposition"
        >
            {/* Background Atmosphere - Cleaner */}
            <div className="absolute inset-0 bg-black -z-20" />

            <div
                className="absolute top-[-20%] right-[-10%] w-[1000px] h-[1000px] rounded-full pointer-events-none -z-10 mix-blend-screen"
                style={{
                    background: 'radial-gradient(circle, rgba(20, 184, 166, 0.08) 0%, transparent 60%)'
                }}
            />
            <div
                className="absolute bottom-[-20%] left-[-10%] w-[1000px] h-[1000px] rounded-full pointer-events-none -z-10 mix-blend-screen"
                style={{
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, transparent 60%)'
                }}
            />

            <div className="container max-w-[1300px] w-full grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">

                {/* LEFT: The Visual Engine - CLEAN & PREMIUM */}
                <motion.div
                    className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)] border border-white/5 bg-zinc-900 group"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                >
                    {/* Glossy Overlay replacing HUD */}
                    <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-tr from-white/5 to-transparent opacity-50" />

                    {/* Premium Lens Flare Effect */}
                    <div className="absolute top-0 right-0 w-2/3 h-2/3 bg-gradient-to-bl from-teal-500/10 to-transparent z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />

                    <motion.img
                        src="/images/Use cases/section-value-prop.jpg"
                        alt="High-fidelity dental simulation"
                        className="w-full h-auto block transform transition-transform duration-[2s] ease-out group-hover:scale-105"
                        width={600}
                        height={750}
                        loading="lazy"
                    />

                    {/* Minimal Label */}
                    <div className="absolute bottom-8 left-8 z-30 flex items-center gap-3">
                        <div className="bg-white/10 backdrop-blur-md border border-white/10 px-4 py-1.5 rounded-full flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.8)]" />
                            <span className="text-[10px] font-bold text-white tracking-widest uppercase">Visual Engine v2.0</span>
                        </div>
                    </div>
                </motion.div>

                {/* RIGHT: The Narrative */}
                <div className="flex flex-col gap-12 relative z-10">

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="text-teal-400 font-mono text-xs tracking-[0.25em] uppercase font-bold flex items-center gap-4 mb-6">
                            <span className="w-8 h-px bg-teal-500/50" />
                            Value Proposition
                        </span>

                        <h2 className="text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1] mb-8 text-balance">
                            Patients don't buy procedures. <br />
                            <span className="text-zinc-500">They buy transformations.</span>
                        </h2>

                        <p className="text-lg text-zinc-400 leading-relaxed max-w-lg">
                            FuturaSmile eliminates the guesswork using a proprietary AI engine designed specifically for dental aesthetics. We bridge the gap between diagnosis and desire.
                        </p>
                    </motion.div>

                    <div className="flex flex-col gap-4">
                        {[
                            {
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.2-2.848.578-4.135" />
                                    </svg>
                                ),
                                title: 'Identity Preservation',
                                description: 'We perfect the smile without altering the face.'
                            },
                            {
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ),
                                title: 'Clinical Accuracy',
                                description: 'Algorithms that respect biological boundaries.'
                            },
                            {
                                icon: (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                    </svg>
                                ),
                                title: 'True Photorealism',
                                description: 'Enamel texture and light reflection that mocks reality.'
                            }
                        ].map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                                className="group p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-white/10 transition-colors duration-300 flex items-start gap-4"
                            >
                                <div className="mt-1 w-10 h-10 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center text-zinc-400 group-hover:text-teal-400 group-hover:border-teal-500/30 transition-all duration-300 shadow-inner">
                                    {feature.icon}
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-white mb-1 group-hover:text-teal-50 transition-colors">
                                        {feature.title}
                                    </h3>
                                    <p className="text-sm text-zinc-500 group-hover:text-zinc-400 transition-colors">
                                        {feature.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
});

ValueProposition.displayName = 'ValueProposition';

export default ValueProposition;
